import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { StepCard } from "./StepCard";
import { EmptyState } from "./EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { S, T } from "@/lib/strings";
import { cn } from "@/lib/utils";
import { stepElapsedMs, type TimelineStep } from "@/lib/steps";
import { Loader2 } from "lucide-react";

/** How close to the bottom still counts as "following the tail". */
const PIN_THRESHOLD_PX = 80;

/**
 * The step timeline, for a finished run and a running one alike.
 *
 * The only thing `live` changes is the tail behaviour. Auto-scroll pins **only**
 * while the reader is already within 80 px of the bottom; the moment they scroll
 * up to read step 7, the pin drops and a pill counts what arrived meanwhile.
 * Yanking someone back to the bottom mid-sentence because a new step landed is
 * the single most infuriating thing a live log can do.
 */
export function StepTimeline({
  steps,
  runId,
  live,
  loading,
  maxSteps,
  className,
}: {
  steps: TimelineStep[];
  runId: string;
  live: boolean;
  loading?: boolean;
  maxSteps?: number;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // A finished run opens at the top, where it reads as a document; only a live
  // one starts pinned to the tail.
  const [pinned, setPinned] = useState(live);
  /** How many steps existed when the reader scrolled away; null while pinned. */
  const [pausedAtCount, setPausedAtCount] = useState<number | null>(null);

  // Derived, not accumulated: no effect can get out of step with the list.
  const unseen = pausedAtCount === null ? 0 : Math.max(0, steps.length - pausedAtCount);

  // The scroll handler needs the current length without re-creating itself on
  // every new step; the ref is written in a layout effect, never during render.
  const stepCountRef = useRef(steps.length);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nowPinned = distance <= PIN_THRESHOLD_PX;
    setPinned(nowPinned);
    // Record the high-water mark at the moment the pin drops, so the pill counts
    // only what arrived after the reader looked away.
    setPausedAtCount(nowPinned ? null : stepCountRef.current);
  }, []);

  // useLayoutEffect so the scroll happens in the same frame the step paints —
  // with useEffect the tail visibly jumps.
  useLayoutEffect(() => {
    const grew = steps.length > stepCountRef.current;
    stepCountRef.current = steps.length;
    if (grew && pinned) scrollToBottom(steps.length <= 1 ? "auto" : "smooth");
  }, [steps.length, pinned, scrollToBottom]);

  if (loading) {
    return (
      <div className={cn("space-y-2", className)} aria-busy="true">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-[86px] w-full" />
        ))}
      </div>
    );
  }

  if (!steps.length) {
    return (
      <EmptyState
        title={live ? S.waitingForSteps : S.empty}
        body={
          live
            ? "裝置已收到指令，第一個 step 完成後會即時出現在這裡。"
            : "這個 run 沒有步驟紀錄。裝置可能在開始前就被略過或取消。"
        }
        compact
        className={className}
      />
    );
  }

  return (
    <div className={cn("relative", className)}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={cn(
          "space-y-2 overflow-y-auto pr-1",
          // A live tail gets a fixed viewport so the pin has something to pin
          // against; a finished run just flows down the page.
          live ? "max-h-[70vh]" : "",
        )}
      >
        {steps.map((step, i) => (
          <StepCard
            key={step.n}
            step={step}
            previous={steps[i - 1]}
            runId={runId}
            elapsedMs={stepElapsedMs(step, steps[i - 1])}
          />
        ))}

        {live && (
          <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-[var(--color-fg-faint)]">
            <Loader2 size={11} className="animate-spin" />
            {maxSteps
              ? T.stepProgress(steps[steps.length - 1]?.n ?? 0, maxSteps)
              : S.waitingForSteps}
          </div>
        )}
      </div>

      {live && !pinned && unseen > 0 && (
        <button
          type="button"
          onClick={() => {
            setPausedAtCount(null);
            setPinned(true);
            scrollToBottom();
          }}
          className={cn(
            "absolute bottom-3 left-1/2 -translate-x-1/2",
            "flex items-center gap-1.5 rounded-full border border-[var(--color-border-strong)]",
            "bg-[var(--color-elevated)] px-3 py-1.5 text-[11px] font-medium text-[var(--color-fg)]",
            "shadow-lg transition-colors hover:bg-[var(--color-elevated-2)]",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]",
          )}
        >
          {S.autoScrollPaused}
          <span className="text-[var(--color-fg-subtle)]">·</span>
          {T.newSteps(unseen)}
          <ArrowDown size={11} />
        </button>
      )}
    </div>
  );
}
